import { Link } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Loader2, ShoppingCart } from "lucide-react";
import { money } from "@/lib/signs";
import { StoreButton } from "@/components/Buttons";
import { SaleChip } from "@/components/SaleTag";
import { GangSavings } from "@/components/GangPrice";

/** One bar under the header: where you came from, what you are building, who makes it, what it costs. Shared by every designer. */
export default function DesignerBar({
    back, backLabel, backTestid = "back-to-category", title, titleTestid, description, store, turnaround, theme,
    price, priceNote, errors = [], busy, blocked, onAdd, adding, sale, gangDtf, prefix = "sign", children,
}) {
    const problems = errors.filter(Boolean);
    return (
        <div className="border-b border-zinc-200 bg-white px-4 sm:px-6" data-testid="designer-bar">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 py-2.5 lg:flex-nowrap">
                <Link to={back} data-testid={backTestid}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-zinc-200 px-3 py-1.5 text-xs font-semibold text-zinc-700 transition-colors hover:border-zinc-400">
                    <ArrowLeft size={13} /> {backLabel}
                </Link>

                <div className="min-w-0 flex-1 basis-full lg:basis-0">
                    <h1 className="truncate font-display text-lg font-black leading-tight tracking-tight text-zinc-900" data-testid={titleTestid || `${prefix}-product-title`}>{title}</h1>
                    <p className="truncate text-[11px] text-zinc-500" data-testid="designer-subline">
                        {description}
                        {store && (
                            <>
                                {description ? " · " : ""}Made by{" "}
                                <Link to={`/stores/${store.slug}`} className="inline-flex items-center gap-0.5 font-semibold hover:underline" style={{ color: theme.ink || theme.primary }} data-testid="designer-store">
                                    {store.verified && <BadgeCheck size={11} />}{store.name}
                                </Link>
                                {store.city ? `, ${store.city}` : ""}
                            </>
                        )}
                        {turnaround ? ` · Ready in ${turnaround}` : ""}
                    </p>
                </div>

                {children}

                <div className="ml-auto text-right">
                    <p className="font-mono text-2xl font-black leading-none" style={{ color: theme.ink || theme.primary }} data-testid={`${prefix}-price`}>
                        {busy ? <Loader2 className="inline animate-spin" size={22} /> : money(price)}
                    </p>
                    {priceNote && <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500" data-testid={`${prefix}-price-note`}>{priceNote}</p>}
                    {problems.length > 0 && (
                        <p className="mt-0.5 text-[11px] font-semibold text-red-600" data-testid={`${prefix}-errors`}>{problems.join(" · ")}</p>
                    )}
                    <div className="flex justify-end gap-2">
                        <SaleChip sale={sale} className="mt-1" testid={`${prefix}-sale-chip`} />
                        <GangSavings amount={price} store={store} dtf={gangDtf} className="mt-1" testid={`${prefix}-gang`} />
                    </div>
                </div>

                <StoreButton theme={theme} onClick={onAdd} disabled={blocked} className="shrink-0 px-5 py-2.5 text-xs" data-testid={`${prefix}-add-to-cart`}>
                    {adding ? <Loader2 className="animate-spin" size={15} /> : <ShoppingCart size={15} />} Add to cart
                </StoreButton>
            </div>
        </div>
    );
}

/** Every material's price per square foot, the picked one lit up. */
export function PriceTable({ variants, variantId, theme }) {
    const rows = variants.filter((v) => v.price_per_sqft);
    if (rows.length < 2) return null;
    return (
        <div className="hidden items-center gap-1 xl:flex" data-testid="price-table">
            {rows.map((v) => (
                <span key={v.id} className="rounded-md border px-2 py-1 font-mono text-[10px] uppercase tracking-wider"
                    style={v.id === variantId ? { borderColor: theme.primary, color: theme.ink || theme.primary, background: "#fff" } : { borderColor: "#e4e4e7", color: "#71717a" }}>
                    {v.material || v.label}{v.print_sides === "double" ? " 2-sided" : ""} <b>${v.price_per_sqft}</b>
                </span>
            ))}
            <span className="ml-1 font-mono text-[10px] uppercase tracking-wider text-zinc-400">/ sqft</span>
        </div>
    );
}

/** Cards: what one sheet costs single or double-sided, and how many pieces that sheet yields at this size. */
export function SheetTable({ product, quote, options, theme }) {
    if (product.pricing_model !== "per_sheet_bulk") return null;
    const rates = quote?.sheet_rates || { single: product.sheet_rate, double: product.sheet_rate_double || product.sheet_rate };
    const double = options.print_sides_opt === "double-sided";
    const lit = (on) => (on ? { borderColor: theme.primary, color: theme.ink || theme.primary, background: "#fff" } : { borderColor: "#e4e4e7", color: "#71717a" });
    const sheet = product.sheet_width_in && product.sheet_height_in ? `${product.sheet_width_in}" × ${product.sheet_height_in}" ` : "";
    return (
        <div className="hidden items-center gap-1 xl:flex" data-testid="sheet-table">
            <span className="rounded-md border px-2 py-1 font-mono text-[10px] uppercase tracking-wider" style={lit(!double)} data-testid="sheet-rate-single">
                Single-sided <b>{money(rates.single)}</b>
            </span>
            <span className="rounded-md border px-2 py-1 font-mono text-[10px] uppercase tracking-wider" style={lit(double)} data-testid="sheet-rate-double">
                Double-sided <b>{money(rates.double)}</b>
            </span>
            <span className="ml-1 font-mono text-[10px] uppercase tracking-wider text-zinc-400" data-testid="sheet-yield">
                per {sheet}sheet{quote?.per_sheet_count ? ` · ${quote.per_sheet_count} per sheet at this size` : ""}
            </span>
        </div>
    );
}
